import { useCallback, useContext } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiContext } from '@shared/contexts';
import { usePollingJob } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';
import { TQueryHookOpts } from '@shared/types';
import { LITERATURE_LIST_QUERY_KEY } from './constants';
import {
  EAssessmentDecision,
  EExclusionReason,
  ELiteratureType,
  EReviewingStage,
  TAssessmentInput,
  TBulkLiteraturesImport,
  TCompleteLiteratureResult,
  TCreateDraftResult,
  TLiterature,
  TLiteratureAssessment,
  TLiteratureContent,
  TLiteratureListResult,
  TSaveLiteratureResult,
  TUseLiteratureResult,
} from './types';

export const useLiteratures = (params: {
  page: number;
  pageSize: number;
  search?: string;
  type?: ELiteratureType[];
  productIds?: string[];
  decision?: EAssessmentDecision;
  reviewingStage?: EReviewingStage;
  enabled?: boolean;
}) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const { page, pageSize, search, type, productIds, decision, reviewingStage, enabled = true } = params;
  const hasProductScope = Boolean(productIds?.length);
  const productIdsKey = productIds?.join(',') ?? '';

  const fetchFn = async (): Promise<TLiteratureListResult | undefined> => {
    if (!page || !pageSize) return undefined;
    const queryParams: Record<string, string | number | string[]> = {
      page: page - 1, // backend is 0-indexed
      pageSize,
    };
    if (search) queryParams.search = search;
    if (type?.length) queryParams.type = type.join(',');
    if (productIds?.length) queryParams.productIds = productIds;
    if (hasProductScope && decision) queryParams.decision = decision;
    if (hasProductScope && reviewingStage) queryParams.reviewingStage = reviewingStage;

    const res = await apiClient.get<TLiteratureListResult>('/literatures', { params: queryParams });
    // Backend stores oldest-first; reverse so versions[0] is the latest completed version.
    res.data.items.forEach((item) => item.versions.reverse());
    return res.data;
  };

  const isFirstPage = !page || page <= 1;
  const effectiveDecision = hasProductScope ? decision : undefined;
  const effectiveReviewingStage = hasProductScope ? reviewingStage : undefined;
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      LITERATURE_LIST_QUERY_KEY,
      page,
      pageSize,
      search,
      type?.join(','),
      productIdsKey,
      effectiveDecision,
      effectiveReviewingStage,
    ],
    queryFn: fetchFn,
    enabled: enabled && !!page && !!pageSize,
    retry,
    refetchOnWindowFocus: isFirstPage,
    refetchOnReconnect: isFirstPage,
    // Keep the previous page's data visible while fetching the next - prevents the
    // table from unmounting and flashing a Spinner on every filter / page change.
    placeholderData: keepPreviousData,
  });

  useHandleQueryError(isError, error, `Failed to load ${TERMINOLOGY.literature.lower} list!`);

  return { data, isLoading, error };
};

export const useLiterature = (id: string, opts?: TQueryHookOpts) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const fetchFn = async (): Promise<TUseLiteratureResult> => {
    const res = await apiClient.get(`/literatures/${id}`);
    res.data.literature.versions.reverse();
    return res.data;
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['literature', id],
    queryFn: fetchFn,
    enabled: opts?.enabled ?? true,
    retry,
  });

  useHandleQueryError(
    isError,
    error,
    `Failed to load ${TERMINOLOGY.literature.lower} ${id}!`,
    `Loading ${TERMINOLOGY.literature.lower} failed!`
  );

  return { data, isLoading, error };
};

export const getDuplicateLiteratureId = (
  apiErr: { data?: Record<string, unknown> | null } | null | undefined
): string | undefined => {
  const id = apiErr?.data?.literatureId;
  return typeof id === 'string' ? id : undefined;
};

// New-tab href (not onClick) so the in-flight dialog state survives and the link stays a real anchor.
const resolveDuplicateLiteratureAction = (apiErr: { data?: Record<string, unknown> | null }) => {
  const existingId = getDuplicateLiteratureId(apiErr);
  return existingId ? { label: 'View existing', href: `/literature/${existingId}` } : undefined;
};

export const useCreateLiterature = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { content: Partial<TLiteratureContent>; file?: File }): Promise<TLiterature> => {
    const formData = new FormData();
    formData.append('json', JSON.stringify({ content: variables.content }));
    if (variables.file) formData.append('file', variables.file, variables.file.name);
    const res = await apiClient.post('/literatures', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Creating ${TERMINOLOGY.literature.lower} failed!`, true, {
      action: resolveDuplicateLiteratureAction,
    }),
    onSuccess: () => {
      show('success', `${TERMINOLOGY.literature.upper} created.`);
      // New item placement depends on current filter - a refetch is safer than an optimistic patch.
      queryClient.invalidateQueries({ queryKey: [LITERATURE_LIST_QUERY_KEY] });
    },
  });
};

export const useSaveLiterature = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: {
    id: string;
    content: { [K in keyof TLiteratureContent]?: TLiteratureContent[K] | null };
    file?: File;
  }): Promise<TSaveLiteratureResult> => {
    const formData = new FormData();
    formData.append('json', JSON.stringify({ content: variables.content }));
    if (variables.file) formData.append('file', variables.file, variables.file.name);
    const res = await apiClient.put(`/literatures/${variables.id}/save`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Saving ${TERMINOLOGY.literature.lower} failed!`, true, {
      action: resolveDuplicateLiteratureAction,
    }),
    onSuccess: ({ draft, stats }, vars) => {
      queryClient.setQueryData(['literature', vars.id], (old: TUseLiteratureResult | undefined) =>
        old ? { ...old, literature: { ...old.literature, draft, stats } } : old
      );
      queryClient.setQueriesData({ queryKey: [LITERATURE_LIST_QUERY_KEY] }, (old: TLiteratureListResult | undefined) =>
        old
          ? { ...old, items: old.items.map((item) => (item._id === vars.id ? { ...item, draft, stats } : item)) }
          : old
      );
    },
  });
};

export const useCompleteLiterature = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (vars: {
    id: string;
    version: string;
    note: string;
  }): Promise<TCompleteLiteratureResult> => {
    const res = await apiClient.put(`/literatures/${vars.id}/complete`, {
      version: vars.version,
      note: vars.note,
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Completing ${TERMINOLOGY.literature.lower} failed!`, true),
    onSuccess: ({ version, stats }, { id }) => {
      queryClient.setQueryData(['literature', id], (old: TUseLiteratureResult | undefined) =>
        old
          ? {
              ...old,
              literature: {
                ...old.literature,
                versions: [version, ...old.literature.versions],
                stats,
                draft: undefined,
              },
            }
          : old
      );
      queryClient.setQueriesData({ queryKey: [LITERATURE_LIST_QUERY_KEY] }, (old: TLiteratureListResult | undefined) =>
        old
          ? {
              ...old,
              items: old.items.map((item) =>
                item._id === id ? { ...item, versions: [version, ...item.versions], stats, draft: undefined } : item
              ),
            }
          : old
      );
      show('success', 'Draft completed.');
    },
  });
};

export const useCreateLiteratureDraft = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (id: string): Promise<TCreateDraftResult> => {
    const res = await apiClient.put(`/literatures/${id}/draft`);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: (error, id, context) => {
      // A 400 "draft already exists" can happen when two users race - force-refetch to clear stale state.
      queryClient.invalidateQueries({ queryKey: ['literature', id] });
      handleApiError(`Creating ${TERMINOLOGY.literature.lower} draft failed!`, true)(error, id, context);
    },
    onSuccess: ({ draft, stats }, id) => {
      show('success', 'Draft created.');
      queryClient.setQueryData(['literature', id], (old: TUseLiteratureResult | undefined) =>
        old ? { ...old, literature: { ...old.literature, draft, stats } } : old
      );
      queryClient.setQueriesData({ queryKey: [LITERATURE_LIST_QUERY_KEY] }, (old: TLiteratureListResult | undefined) =>
        old
          ? {
              ...old,
              items: old.items.map((item) => (item._id === id ? { ...item, draft, stats } : item)),
            }
          : old
      );
    },
  });
};

export const useArchiveLiterature = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (id: string): Promise<void> => {
    await apiClient.delete(`/literatures/${id}`);
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Archiving ${TERMINOLOGY.literature.lower} failed!`, true),
    onSuccess: (_, id) => {
      show('success', `${TERMINOLOGY.literature.upper} archived.`);
      // Page totals change after archive; remove detail entry to prevent reopening the stale record.
      queryClient.removeQueries({ queryKey: ['literature', id] });
      queryClient.invalidateQueries({ queryKey: [LITERATURE_LIST_QUERY_KEY] });
    },
  });
};

export const useLookupLiterature = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (query: string): Promise<Partial<TLiteratureContent>> => {
    const res = await apiClient.post('/literatures/lookup', { query });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Lookup failed!', true, { action: resolveDuplicateLiteratureAction }),
  });
};

export const useCreateAssessment = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (
    variables: TAssessmentInput & {
      literatureId: string;
      // Caller passes a name map for any newly-referenced product - the create response only returns the assessment,
      // so without this the AssessmentsTab renders raw IDs for products not already in literature.resolvedItems.
      productNames: Record<string, string>;
    }
  ): Promise<TLiteratureAssessment> => {
    const { literatureId, productNames: _productNames, ...body } = variables;
    const res = await apiClient.post(`/literatures/${literatureId}/assessments`, body);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Creating assessment failed!', true),
    onSuccess: (assessment, vars) => {
      show('success', 'Assessment created.');
      queryClient.setQueryData(['literature', vars.literatureId], (old: TUseLiteratureResult | undefined) =>
        old
          ? {
              ...old,
              literature: { ...old.literature, assessments: [...old.literature.assessments, assessment] },
              resolvedItems: {
                ...old.resolvedItems,
                ...Object.fromEntries(vars.products.map((id) => [id, { name: vars.productNames[id] ?? id }])),
              },
            }
          : old
      );
      // Assessment counts on the list are server-computed per product - refetch instead of patching.
      queryClient.invalidateQueries({ queryKey: [LITERATURE_LIST_QUERY_KEY] });
    },
  });
};

export const useUpdateAssessment = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: {
    literatureId: string;
    assessmentId: string;
    products: string[];
    decision: EAssessmentDecision;
    reviewingStage?: EReviewingStage | null;
    exclusionReason?: EExclusionReason | null;
    comment?: string | null;
    // See useCreateAssessment: response only returns the assessment, caller must supply names for any newly-added product.
    productNames: Record<string, string>;
  }): Promise<TLiteratureAssessment> => {
    const { literatureId, assessmentId, productNames: _productNames, ...body } = variables;
    const res = await apiClient.put(`/literatures/${literatureId}/assessments/${assessmentId}`, body);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Updating assessment failed!', true),
    onSuccess: (assessment, vars) => {
      show('success', 'Assessment updated.');
      queryClient.setQueryData(['literature', vars.literatureId], (old: TUseLiteratureResult | undefined) =>
        old
          ? {
              ...old,
              literature: {
                ...old.literature,
                assessments: old.literature.assessments.map((a) => (a._id === vars.assessmentId ? assessment : a)),
              },
              resolvedItems: {
                ...old.resolvedItems,
                ...Object.fromEntries(vars.products.map((id) => [id, { name: vars.productNames[id] ?? id }])),
              },
            }
          : old
      );
      queryClient.invalidateQueries({ queryKey: [LITERATURE_LIST_QUERY_KEY] });
    },
  });
};

export const useArchiveAssessment = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { literatureId: string; assessmentId: string }): Promise<void> => {
    await apiClient.delete(`/literatures/${variables.literatureId}/assessments/${variables.assessmentId}`);
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Archiving assessment failed!', true),
    onSuccess: (_, vars) => {
      show('success', 'Assessment archived.');
      queryClient.setQueryData(['literature', vars.literatureId], (old: TUseLiteratureResult | undefined) =>
        old
          ? {
              ...old,
              literature: {
                ...old.literature,
                assessments: old.literature.assessments.map((a) =>
                  a._id === vars.assessmentId ? { ...a, archived: true } : a
                ),
              },
            }
          : old
      );
      queryClient.invalidateQueries({ queryKey: [LITERATURE_LIST_QUERY_KEY] });
    },
  });
};

export const usePollBulkLiteraturesImport = (
  onCompleted?: (job: TBulkLiteraturesImport) => void,
  onFailed?: (job: TBulkLiteraturesImport) => void
) => {
  const queryClient = useQueryClient();
  return usePollingJob<TBulkLiteraturesImport>({
    queryKeyPrefix: 'bulk-literature-import',
    endpoint: '/literatures/import',
    progressMessage: `Importing ${TERMINOLOGY.literature.lower}...`,
    // The per-bucket breakdown lives in the result dialog. Toast stays minimal - if the dialog doesn't
    // open, everything imported cleanly and the count is redundant.
    successMessage: 'Import complete.',
    failureMessage: `${TERMINOLOGY.literature.upper} import failed.`,
    onCompleted: (job) => {
      queryClient.invalidateQueries({ queryKey: [LITERATURE_LIST_QUERY_KEY] });
      onCompleted?.(job);
    },
    onFailed,
  });
};

export const useImportBulkLiteratures = (setJobId: (id: string) => void, startProgress: () => () => void) => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const mutationFn = useCallback(
    async (variables: { files: File[]; assessment?: TAssessmentInput }): Promise<TBulkLiteraturesImport> => {
      const formData = new FormData();
      variables.files.forEach((f) => formData.append('files', f, f.name));
      if (variables.assessment) {
        formData.append('json', JSON.stringify({ assessment: variables.assessment }));
      }
      const cancelProgress = startProgress();
      try {
        const res = await apiClient.post('/literatures/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return res.data;
      } catch (e) {
        cancelProgress();
        throw e;
      }
    },
    [startProgress]
  );
  return useMutation({
    mutationFn,
    onSuccess: (job) => setJobId(job._id),
    onError: handleApiError(`${TERMINOLOGY.literature.upper} import failed!`, true),
  });
};
